/**
 * cart-drawer.js — Ajax Cart Drawer Custom Element
 * Fetch API + Section Rendering API (no page reloads)
 */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-cart-overlay]');
    this.panel = this.querySelector('.cart-drawer__panel');

    this._onKeydown = this._onKeydown.bind(this);
    this._onCartRefresh = () => this.refresh();
    this._onCartOpen = () => { this.refresh(); this.open(); };

    this.bindEvents();
    this.bindGlobalTriggers();
    this._maybeHideBundleSuggestion();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:refresh', this._onCartRefresh);
    document.removeEventListener('cart:open', this._onCartOpen);
  }

  /* ── Event Binding ── */

  bindEvents() {
    this.overlay?.addEventListener('click', () => this.close());

    this.querySelectorAll('[data-cart-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    /* Delegate clicks inside the drawer */
    this.addEventListener('click', (e) => this.handleClick(e));
  }

  bindGlobalTriggers() {
    /* Header cart icon (and any other toggle) */
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    /* Custom events dispatched by product-form, quick-add, etc. */
    document.addEventListener('cart:refresh', this._onCartRefresh);
    document.addEventListener('cart:open', this._onCartOpen);
  }

  /* ── Open / Close ── */

  open() {
    this.removeAttribute('hidden');
    /* Force reflow so the CSS transition plays */
    void this.offsetHeight;
    this.setAttribute('open', '');
    document.body.classList.add('cart-drawer-open');
    document.addEventListener('keydown', this._onKeydown);
    this.querySelector('[data-cart-close]')?.focus();
  }

  close() {
    this.removeAttribute('open');
    document.body.classList.remove('cart-drawer-open');
    document.removeEventListener('keydown', this._onKeydown);

    /* Restore focus to the element that opened the drawer */
    const trigger = document.querySelector('[data-cart-drawer-toggle]');
    if (trigger) trigger.focus();

    const onEnd = () => {
      if (!this.hasAttribute('open')) {
        this.setAttribute('hidden', '');
      }
    };
    this.panel?.addEventListener('transitionend', onEnd, { once: true });
    /* Fallback in case transitionend doesn't fire */
    setTimeout(onEnd, 350);
  }

  /* ── Keyboard Handling (Escape + Focus Trap) ── */

  _onKeydown(e) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }

    if (e.key !== 'Tab') return;

    /* Focus trap: keep Tab cycling within the panel */
    const focusable = this.panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ── Click Delegation ── */

  handleClick(e) {
    /* Remove item */
    const removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      e.preventDefault();
      this.updateQuantity(parseInt(removeBtn.dataset.line, 10), 0);
      return;
    }

    /* Quantity +/- */
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      const line = parseInt(qtyBtn.dataset.line, 10);
      const item = qtyBtn.closest('[data-cart-item]');
      const currentQty = parseInt(
        item.querySelector('[data-qty-value]').textContent,
        10
      );
      const change = parseInt(qtyBtn.dataset.qtyChange, 10);
      this.updateQuantity(line, Math.max(0, currentQty + change));
      return;
    }

    /* Checkout: native form submission via {% form 'cart' %} mit name="checkout".
       KEIN preventDefault — Shopify submitted das Form an /cart und redirected zu /checkout.
       Robuster als window.location.href Hijack (cart-properties bleiben erhalten). */

    /* Abo aktivieren (ZUM ABO Button) */
    const subActivateBtn = e.target.closest('[data-cart-sub-activate]');
    if (subActivateBtn) {
      e.preventDefault();
      if (subActivateBtn.disabled) return;
      const planId = subActivateBtn.dataset.sellingPlanId;
      if (!planId) return;
      this.applySubscription(planId);
      return;
    }

    /* Abo entfernen (Entfernen-Link im aktiven Zustand) */
    const subRemoveBtn = e.target.closest('[data-cart-sub-remove]');
    if (subRemoveBtn) {
      e.preventDefault();
      this.applySubscription('');
      return;
    }

    /* Abo-Intervall wechseln (6 Monate / 12 Monate Pills).
       Sprint 015o: Optimistic UI — Pill SOFORT visuell aktiv-toggeln und
       skipValidation, weil Pills nur valid plans rendern (Liquid-side bereits
       validiert). Spart 163ms /products fetch + visueller 0ms-Lag. */
    const subIntervalBtn = e.target.closest('[data-cart-sub-interval]');
    if (subIntervalBtn) {
      e.preventDefault();
      const planId = subIntervalBtn.dataset.sellingPlanId;
      if (!planId) return;
      /* Optimistic: alle Geschwister-Pills entaktivieren, geklickte aktivieren */
      const siblings = subIntervalBtn.parentElement?.querySelectorAll('[data-cart-sub-interval]');
      siblings?.forEach((p) => {
        p.classList.remove('cart-drawer__sub-interval--active');
        p.setAttribute('aria-checked', 'false');
      });
      subIntervalBtn.classList.add('cart-drawer__sub-interval--active');
      subIntervalBtn.setAttribute('aria-checked', 'true');
      /* Sprint 020v: skipValidation FALSE — Multi-Item Carts können Line-Items
         mit verschiedenen Varianten haben. Jede Variante hat eigene Plan-IDs
         (auch bei gleichem Namen "6 Monate"). Validation mapped per-Item auf
         die korrekte variant-spezifische Plan-ID. */
      this.applySubscription(planId, { skipValidation: false, optimistic: true });
      return;
    }

    /* Bundle-Suggestion: Multi-Picker +/- (5+3 Mode) */
    const multiBtn = e.target.closest('[data-bundle-multi-change]');
    if (multiBtn) {
      e.preventDefault();
      if (multiBtn.disabled) return;
      const banner = multiBtn.closest('[data-bundle-suggestion]');
      const change = parseInt(multiBtn.dataset.bundleMultiChange, 10);
      const scent = multiBtn.dataset.scent;
      this._updateBundleMulti(banner, scent, change);
      return;
    }

    /* Bundle-Suggestion: GRATIS-PACK HINZUFÜGEN klick → Singles entfernen, Bundle hinzufügen */
    const bundleUpgradeBtn = e.target.closest('[data-bundle-upgrade]');
    if (bundleUpgradeBtn) {
      e.preventDefault();
      if (bundleUpgradeBtn.disabled) return;
      const banner = bundleUpgradeBtn.closest('[data-bundle-suggestion]');
      const variantId = banner?.dataset.bundleVariantId;
      if (!variantId) return;

      /* Multi-Mode (5+3): aggregierte counts aus pickern */
      const multi = banner.querySelector('[data-bundle-multi]');
      if (multi) {
        const target = parseInt(multi.dataset.target, 10);
        const total = this._getBundleMultiTotal(banner);
        if (total !== target) return; /* CTA sollte disabled sein */
        this.upgradeToBundleMulti(banner, variantId);
        return;
      }

      /* Single-Mode (3+1): selected radio */
      const selectedScent = banner.querySelector('[data-bundle-scent]:checked')?.value;
      if (!selectedScent) return;
      this.upgradeToBundle(banner, selectedScent, variantId);
      return;
    }

    /* Bundle-Suggestion: "Nein danke" → Session-Storage dismiss */
    const bundleDismissBtn = e.target.closest('[data-bundle-dismiss]');
    if (bundleDismissBtn) {
      e.preventDefault();
      try { sessionStorage.setItem('waschma_bundle_suggestion_dismissed', '1'); } catch (_) {}
      const banner = bundleDismissBtn.closest('[data-bundle-suggestion]');
      if (banner) banner.remove();
      return;
    }
  }

  /* ── Bundle-Upgrade (3 Single-Packs → 3+1 Bundle Conversion) ──
     Architektur-Hinweis: Alle Single-Packs sind Variants von 'mix-match-waschstreifen'
     mit Variant-Title "1 Packung" und Duft als Line-Item-Property.

     Flow:
     1. Lese aktuelle Scent-Counts aus data-attributes (vom Liquid berechnet via properties × qty)
     2. Erhöhe gewählten Duft um +1 (= GRATIS-Pack)
     3. Entferne alle "1 Packung" Variant-Lines aus Cart (Reverse-Order)
     4. Füge 1× 3+1 Variant hinzu mit Properties {Duftname: Anzahl, ...}
     5. Refresh + Toast
  */
  async upgradeToBundle(banner, selectedScent, variantId) {
    this.setAttribute('data-updating', '');
    const ctaBtn = banner.querySelector('[data-bundle-upgrade]');
    if (ctaBtn) ctaBtn.disabled = true;

    try {
      /* free_count: 1 für 3+1 Bundle, 3 für 5+3 Bundle. Alle GRATIS-Packs gehen in den gewählten Duft. */
      const freeCount = parseInt(banner.dataset.freeCount, 10) || 1;

      const scentCounts = {
        Duftfrei: parseInt(banner.dataset.currentDuftfrei, 10) || 0,
        'Frühlingsfrische': parseInt(banner.dataset.currentFruehlingsfrische, 10) || 0,
        Ozeanfrische: parseInt(banner.dataset.currentOzeanfrische, 10) || 0,
      };
      scentCounts[selectedScent] = (scentCounts[selectedScent] || 0) + freeCount;

      const cartRes = await fetch('/cart.js');
      if (!cartRes.ok) throw new Error('Cart fetch failed');
      const cart = await cartRes.json();

      /* Single-Pack-Items: handle == 'mix-match-waschstreifen' UND variant title enthält "1 Packung" */
      const singlePackLines = [];
      cart.items.forEach((item, idx) => {
        const handle = item.product_handle || item.handle || '';
        const variantTitle = (item.variant_title || '').toLowerCase();
        if (handle === 'mix-match-waschstreifen' && variantTitle.includes('1 packung')) {
          singlePackLines.push(idx + 1);
        }
      });

      /* Reverse-Order weil Line-Index shiftet bei Removal */
      for (const line of singlePackLines.sort((a, b) => b - a)) {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: 0 }),
        });
        if (!res.ok) console.error('[cart-drawer] failed to remove single-pack line', line);
      }

      /* Properties: nur Düfte mit count > 0 setzen, als String */
      const properties = {};
      for (const [scent, count] of Object.entries(scentCounts)) {
        if (count > 0) properties[scent] = String(count);
      }

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1,
          properties,
        }),
      });
      if (!addRes.ok) {
        const errJson = await addRes.json().catch(() => null);
        throw new Error('Bundle add failed: ' + (errJson?.description || addRes.status));
      }

      await this.refresh();
      const toastMsg = freeCount > 1
        ? `Du hast ${freeCount} Packungen gratis dazu bekommen! 🎁`
        : 'Du hast 1 Packung gratis dazu bekommen! 🎁';
      this.showToast(toastMsg);
    } catch (err) {
      console.error('[cart-drawer] bundle upgrade error:', err);
      this.showToast('Upgrade fehlgeschlagen, bitte erneut versuchen.', true);
      if (ctaBtn) ctaBtn.disabled = false;
    } finally {
      this.removeAttribute('data-updating');
    }
  }

  /* ── Multi-Picker (5+3 Mode) ──
     Aktualisiert Count pro Duft, prüft Total, syncronisiert Button-Disabled-States. */
  _updateBundleMulti(banner, scent, change) {
    const valueEl = banner.querySelector(`[data-bundle-multi-value][data-scent="${scent}"]`);
    if (!valueEl) return;
    let current = parseInt(valueEl.textContent, 10) || 0;
    const target = parseInt(banner.querySelector('[data-bundle-multi]').dataset.target, 10);
    const totalBefore = this._getBundleMultiTotal(banner);

    if (change > 0 && totalBefore >= target) return;
    if (change < 0 && current <= 0) return;

    current += change;
    if (current < 0) current = 0;
    valueEl.textContent = current;
    if (current === 0) {
      valueEl.setAttribute('data-zero', '');
    } else {
      valueEl.removeAttribute('data-zero');
    }

    this._syncBundleMultiButtons(banner);
    this._updateBundleMultiStatus(banner);
  }

  _getBundleMultiTotal(banner) {
    let total = 0;
    banner.querySelectorAll('[data-bundle-multi-value]').forEach((el) => {
      total += parseInt(el.textContent, 10) || 0;
    });
    return total;
  }

  _syncBundleMultiButtons(banner) {
    const target = parseInt(banner.querySelector('[data-bundle-multi]').dataset.target, 10);
    const total = this._getBundleMultiTotal(banner);
    const reachedTarget = total >= target;

    banner.querySelectorAll('[data-bundle-multi-change]').forEach((btn) => {
      const change = parseInt(btn.dataset.bundleMultiChange, 10);
      const scent = btn.dataset.scent;
      const valueEl = banner.querySelector(`[data-bundle-multi-value][data-scent="${scent}"]`);
      const value = parseInt(valueEl?.textContent || '0', 10);

      /* Disabled-States für sold-out bleiben unverändert via Liquid */
      const row = btn.closest('.cart-drawer__bundle-multi-row');
      const isSoldOut = row?.classList.contains('cart-drawer__bundle-multi-row--disabled');
      if (isSoldOut) {
        if (change > 0) btn.disabled = true;
        return;
      }

      if (change > 0) {
        btn.disabled = reachedTarget;
      } else {
        btn.disabled = value <= 0;
      }
    });
  }

  _updateBundleMultiStatus(banner) {
    const target = parseInt(banner.querySelector('[data-bundle-multi]').dataset.target, 10);
    const total = this._getBundleMultiTotal(banner);
    const statusEl = banner.querySelector('[data-bundle-multi-status]');
    if (statusEl) {
      statusEl.innerHTML = `<strong>${total}</strong> / ${target} verteilt`;
      if (total === target) {
        statusEl.setAttribute('data-complete', '');
      } else {
        statusEl.removeAttribute('data-complete');
      }
    }
    /* CTA-Button enable/disable */
    const cta = banner.querySelector('[data-bundle-upgrade]');
    if (cta) cta.disabled = total !== target;
  }

  /* Cart-Conversion für Multi-Mode (5+3): liest pro Duft die gewünschte Anzahl */
  async upgradeToBundleMulti(banner, variantId) {
    this.setAttribute('data-updating', '');
    const ctaBtn = banner.querySelector('[data-bundle-upgrade]');
    if (ctaBtn) ctaBtn.disabled = true;

    try {
      const scentCounts = {
        Duftfrei: parseInt(banner.dataset.currentDuftfrei, 10) || 0,
        'Frühlingsfrische': parseInt(banner.dataset.currentFruehlingsfrische, 10) || 0,
        Ozeanfrische: parseInt(banner.dataset.currentOzeanfrische, 10) || 0,
      };
      banner.querySelectorAll('[data-bundle-multi-value]').forEach((el) => {
        const scent = el.dataset.scent;
        const add = parseInt(el.textContent, 10) || 0;
        scentCounts[scent] = (scentCounts[scent] || 0) + add;
      });

      const cartRes = await fetch('/cart.js');
      if (!cartRes.ok) throw new Error('Cart fetch failed');
      const cart = await cartRes.json();

      const singlePackLines = [];
      cart.items.forEach((item, idx) => {
        const handle = item.product_handle || item.handle || '';
        const variantTitle = (item.variant_title || '').toLowerCase();
        if (handle === 'mix-match-waschstreifen' && variantTitle.includes('1 packung')) {
          singlePackLines.push(idx + 1);
        }
      });

      for (const line of singlePackLines.sort((a, b) => b - a)) {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: 0 }),
        });
        if (!res.ok) console.error('[cart-drawer] failed to remove single-pack line', line);
      }

      const properties = {};
      for (const [scent, count] of Object.entries(scentCounts)) {
        if (count > 0) properties[scent] = String(count);
      }

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1, properties }),
      });
      if (!addRes.ok) {
        const errJson = await addRes.json().catch(() => null);
        throw new Error('Bundle add failed: ' + (errJson?.description || addRes.status));
      }

      const freeCount = parseInt(banner.dataset.freeCount, 10) || 1;
      await this.refresh();
      this.showToast(`Du hast ${freeCount} Packungen gratis dazu bekommen! 🎁`);
    } catch (err) {
      console.error('[cart-drawer] bundle upgrade (multi) error:', err);
      this.showToast('Upgrade fehlgeschlagen, bitte erneut versuchen.', true);
      if (ctaBtn) ctaBtn.disabled = false;
    } finally {
      this.removeAttribute('data-updating');
    }
  }

  /* Toast: kurze Erfolgsmeldung am unteren Viewport-Rand.
     Sprint 015c Bug-Fix (2026-06-08): Element wird nach Slide-Out komplett aus DOM
     entfernt, NICHT mehr persistent als versteckter Div in body belassen.
     Sprint 015l Bug-Fix (2026-06-08): Double-rAF + Force-Layout für flüssige
     Slide-In-Animation. Vorheriges Pattern mit single rAF triggerte Transition
     bevor der initial CSS-State (translateY(120%)) vom Browser gerendert war
     → "Jump"-Animation statt smooth Slide-In. Force-Layout via offsetHeight
     erzwingt initial-render, doppeltes rAF garantiert dass nächste Frame
     die Transition korrekt startet. */
  showToast(message, isError = false) {
    let toast = document.querySelector('.cart-drawer__toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'cart-drawer__toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.backgroundColor = isError ? 'var(--color-duftfrei)' : '';

    /* Beide Timeouts resetten — rapid-fire showToast soll vorheriges Cleanup nicht killen */
    clearTimeout(this._toastTimeout);
    clearTimeout(this._toastRemoveTimeout);

    /* Force-Layout: Browser muss initial-CSS-State (translateY(120%) off-screen)
       gerendert haben BEVOR data-visible transition triggert. */
    void toast.offsetHeight;

    /* Double-rAF: erstes rAF wartet auf nächste Frame, zweites garantiert dass
       Browser den initial-state ge-paint hat bevor Transition startet. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.setAttribute('data-visible', '');
      });
    });

    this._toastTimeout = setTimeout(() => {
      toast.removeAttribute('data-visible');
      /* Nach Slide-Out-Animation (300ms transition + 100ms buffer) Element komplett removen */
      this._toastRemoveTimeout = setTimeout(() => {
        if (toast.parentNode && !toast.hasAttribute('data-visible')) {
          toast.remove();
        }
      }, 400);
    }, 2800);
  }

  /* Hide Bundle-Suggestion-Banner wenn User in dieser Session dismissed hat */
  _maybeHideBundleSuggestion() {
    try {
      if (sessionStorage.getItem('waschma_bundle_suggestion_dismissed') === '1') {
        const banner = this.querySelector('[data-bundle-suggestion]');
        if (banner) banner.remove();
      }
    } catch (_) {}
  }

  /* ── Subscription Toggle (Selling-Plan auf alle Cart-Items anwenden) ──
     Uses item.key (eindeutig pro Line). null statt empty-string für Removal.
     parseInt für selling_plan weil Cart-API Integer erwartet.

     Sprint 015o (2026-06-09): Speed-Optimierung:
     - opts.skipValidation: skip /products/{handle}.js fetch wenn Plan bereits
       als valid bekannt ist (z.B. bei Pill-Switch — Pills sind im Liquid nur
       gerendert für valid Plans). Spart 163ms pro Call.
     - opts.optimistic: skip data-updating Attribute (UI bereits visuell
       gewechselt). User sieht 0ms visuelle Verzögerung.
     - Parallel change.js Calls via Promise.all (saves time bei multi-item Carts).
     - this._planAllocCache als instance-property → cache überlebt zwischen calls. */
  async applySubscription(sellingPlanId, opts = {}) {
    const { skipValidation = false, optimistic = false } = opts;
    if (!optimistic) this.setAttribute('data-updating', '');
    try {
      const cartRes = await fetch('/cart.js');
      if (!cartRes.ok) throw new Error('Cart fetch failed: ' + cartRes.status);
      const cart = await cartRes.json();

      /* sellingPlanId leer = Removal → null, sonst int */
      const requestedPlanIdInt = sellingPlanId ? parseInt(sellingPlanId, 10) : null;

      /* Instance-Cache (überlebt zwischen applySubscription-Calls) */
      this._planAllocCache = this._planAllocCache || {};
      const productAllocCache = this._planAllocCache;
      const getAllocationsForItem = async (item) => {
        const handle = item.product_handle || item.handle || '';
        if (!handle) return [];
        if (!productAllocCache[handle]) {
          try {
            const pRes = await fetch(`/products/${handle}.js`);
            if (!pRes.ok) { productAllocCache[handle] = {}; return []; }
            const pData = await pRes.json();
            const map = {};
            (pData.variants || []).forEach((v) => {
              map[v.id] = (v.selling_plan_allocations || []).map((a) => a.selling_plan_id);
            });
            productAllocCache[handle] = map;
          } catch (_) {
            productAllocCache[handle] = {};
            return [];
          }
        }
        const variantId = item.variant_id || item.id;
        return productAllocCache[handle][variantId] || [];
      };

      /* Parallel change.js calls (statt sequential for-of) */
      const changes = cart.items.map(async (item) => {
        let itemPlanId = requestedPlanIdInt;

        if (requestedPlanIdInt !== null && !skipValidation) {
          const allocPlanIds = await getAllocationsForItem(item);
          if (!allocPlanIds.includes(requestedPlanIdInt)) {
            if (allocPlanIds.length === 0) return; /* skip item */
            itemPlanId = parseInt(allocPlanIds[0], 10);
          }
        }

        const body = {
          id: item.key,
          quantity: item.quantity,
          selling_plan: itemPlanId
        };
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const json = await res.json().catch(() => null);
          console.error('[cart-drawer] selling_plan update failed:', item.key, res.status, json);
        }
      });
      await Promise.all(changes);

      await this.refresh();
    } catch (err) {
      console.error('[cart-drawer] subscription change error:', err);
    } finally {
      if (!optimistic) this.removeAttribute('data-updating');
    }
  }

  /* ── Cart API ── */

  async updateQuantity(line, quantity) {
    const item = this.querySelector(
      `[data-cart-item][data-line="${line}"]`
    );
    if (item) item.setAttribute('data-updating', '');

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      if (!res.ok) throw new Error(res.status);

      const cart = await res.json();
      this.updateCartCount(cart);
      await this.renderSection();
    } catch (err) {
      console.error('[cart-drawer] update error:', err);
      if (item) item.removeAttribute('data-updating');
    }
  }

  /* ── Section Rendering API ──
     Sprint 015o (2026-06-09): renderSection() bleibt für Backwards-Compatibility
     (wird von updateQuantity etc. genutzt). Neue _applySectionData(html) Method
     für parallel-fetch-Optimierung in refresh(). */

  async renderSection() {
    try {
      const res = await fetch('/?sections=cart-drawer');
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      this._applySectionData(data['cart-drawer']);
    } catch (err) {
      console.error('[cart-drawer] render error:', err);
    }
  }

  _applySectionData(html) {
    if (!html) return;
    const temp = document.createElement('div');
    temp.innerHTML = html;

    /* Sprint 015q (2026-06-09): Statische Payment-Icons VOR innerHTML-Replace
       extrahieren und nach Replace die alten DOM-Nodes zurück-insertieren.
       Verhindert dass Browser die <img>-Decode-Pipeline für jedes der 9
       Payment-Icon-SVGs neu starten muss → eliminiert "Blink"-Effekt beim
       ersten Switch (Decode-Pipeline kalt). */
    const existingPaymentIcons = this.querySelector('.cart-drawer__payment-icons');

    /* Replace scrollable content (items + empty state + shipping bar) */
    const newContent = temp.querySelector('[data-cart-content]');
    const currentContent = this.querySelector('[data-cart-content]');
    if (newContent && currentContent) {
      currentContent.innerHTML = newContent.innerHTML;
    }

    /* Bundle-Suggestion: respect session-dismiss after re-render */
    this._maybeHideBundleSuggestion();

    /* Replace sticky footer (may appear or disappear) */
    const currentFooter = this.querySelector('[data-cart-footer]');
    const newFooter = temp.querySelector('[data-cart-footer]');

    if (newFooter && currentFooter) {
      currentFooter.outerHTML = newFooter.outerHTML;
    } else if (newFooter && !currentFooter) {
      this.panel.appendChild(newFooter);
    } else if (!newFooter && currentFooter) {
      currentFooter.remove();
    }

    /* Sprint 015q: Payment-Icons zurück-insertieren — die alten <img> Nodes
       behalten ihren image-decode-Cache, neue Nodes vom innerHTML-Replace
       würden den Decoder neu starten → Flash. */
    if (existingPaymentIcons) {
      const newPaymentsSlot = this.querySelector('.cart-drawer__payment-icons');
      if (newPaymentsSlot && newPaymentsSlot !== existingPaymentIcons) {
        newPaymentsSlot.replaceWith(existingPaymentIcons);
      }
    }
  }

  /* ── Header Badge ── */

  updateCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = cart.item_count;
      badge.classList.toggle(
        'header__cart-count--hidden',
        cart.item_count === 0
      );
    });
  }

  /* ── Full Refresh (after external cart changes) ──
     Sprint 015o (2026-06-09): /cart.js + /?sections=cart-drawer parallel
     statt sequential. Spart ~160ms pro refresh-Call. */

  async refresh() {
    try {
      const [cartRes, sectionRes] = await Promise.all([
        fetch('/cart.js'),
        fetch('/?sections=cart-drawer')
      ]);
      if (cartRes.ok) {
        const cart = await cartRes.json();
        this.updateCartCount(cart);
      }
      if (sectionRes.ok) {
        const data = await sectionRes.json();
        this._applySectionData(data['cart-drawer']);
      }
    } catch (err) {
      console.error('[cart-drawer] refresh error:', err);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);
